import {testIdProps} from '@github-ui/test-id-props'
import {ActionList, Link} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {memo, useEffect, useRef, useState} from 'react'

import type {IssueField} from '../api/common-contracts'
import {getInitialState} from '../helpers/initial-state'
import {useViews} from '../hooks/use-views'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import styles from './add-issue-fields-dialog.module.css'
import {AddIssueFieldsDialogBanner} from './add-issue-fields-dialog-banner'
import {getValidationResult, ValidationResult} from './add-issue-fields-dialog-validation'
import {getColumnIcon} from './column-detail-helpers'
import {useAddField} from './react_table/hooks/use-add-field'

type AddIssueFieldsDialogProps = {
  isOpen: boolean
  setOpen: (open: boolean) => void
  issueFields: Array<IssueField>
  openAddColumnDialog: () => void
}

export const AddIssueFieldsDialog = memo<AddIssueFieldsDialogProps>(function AddIssueFieldsDialog({
  isOpen,
  setOpen,
  issueFields,
  openAddColumnDialog,
}) {
  const {importIssueFields} = useAddField()
  const {currentView} = useViews()
  const {allColumns} = useAllColumns()

  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(() => new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasImportError, setHasImportError] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const projectFieldCount = allColumns?.length || 0
  const selectedCount = selectedFieldIds.size
  const {projectOwner} = getInitialState()
  const ownerName = projectOwner?.name?.toLowerCase()

  const validationResult = getValidationResult(selectedCount, projectFieldCount)
  const canSubmit = validationResult === ValidationResult.Valid && !isSubmitting

  const visibleIssueFieldsRef = useRef<Array<IssueField>>(issueFields)
  const visibleIssueFields = visibleIssueFieldsRef.current

  useEffect(() => {
    if (isOpen && !isSubmitting) {
      visibleIssueFieldsRef.current = issueFields
    }
  }, [isOpen, isSubmitting, issueFields])

  useEffect(() => {
    if (isOpen) {
      setSelectedFieldIds(new Set())
      setHasImportError(false)
      setHasAttemptedSubmit(false)
    }
  }, [isOpen])

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true)

    if (!canSubmit || !currentView) return

    setHasImportError(false)
    setIsSubmitting(true)

    try {
      await importIssueFields.perform({
        issueFieldIds: Array.from(selectedFieldIds),
        viewNumber: currentView.number,
      })
      if (importIssueFields.status.current.status === 'failed') {
        setHasImportError(true)
        setIsSubmitting(false)
        return
      }
      setSelectedFieldIds(new Set())
      setOpen(false)
    } catch {
      setHasImportError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setSelectedFieldIds(new Set())
    setHasImportError(false)
    setHasAttemptedSubmit(false)
    setOpen(false)
  }

  const handleCreateProjectField = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    openAddColumnDialog()
  }

  if (!isOpen) return null

  const showBanners = !isSubmitting || hasImportError

  return (
    <Dialog
      className={styles.dialog}
      title="Add issue fields"
      subtitle={
        <>
          Keep your work consistent with issue fields defined by {ownerName}. Need something project specific?{' '}
          <Link as="button" inline onClick={handleCreateProjectField} {...testIdProps('create-project-field-link')}>
            Create a project field
          </Link>
        </>
      }
      onClose={handleCancel}
      height="auto"
      width="large"
      {...testIdProps('add-issue-fields-dialog')}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: handleCancel},
        {
          buttonType: 'primary',
          content: 'Add',
          onClick: handleSubmit,
          loading: isSubmitting,
          ...testIdProps('add-issue-fields-submit-button'),
        },
      ]}
      renderBody={() => (
        <>
          {showBanners && (
            <AddIssueFieldsDialogBanner
              projectCount={projectFieldCount}
              selectedCount={selectedCount}
              hasAttemptedSubmit={hasAttemptedSubmit}
              hasImportError={hasImportError}
              onRetry={handleSubmit}
            />
          )}

          {visibleIssueFields.length > 0 ? (
            <ActionList
              className={styles.dialogBody}
              showDividers
              selectionVariant="multiple"
              role="listbox"
              {...testIdProps('add-issue-fields-list')}
            >
              {visibleIssueFields.map(field => {
                const Icon = getColumnIcon(field.dataType)
                const isSelected = selectedFieldIds.has(field.id)

                return (
                  <ActionList.Item
                    key={field.id}
                    role="option"
                    aria-selected={isSelected}
                    selected={isSelected}
                    onSelect={e => {
                      e.stopPropagation()
                      setSelectedFieldIds(prev => {
                        const newSet = new Set(prev)
                        if (newSet.has(field.id)) newSet.delete(field.id)
                        else newSet.add(field.id)
                        return newSet
                      })
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <Icon />
                    </ActionList.LeadingVisual>
                    {field.name}
                  </ActionList.Item>
                )
              })}
            </ActionList>
          ) : (
            <div className={styles.dialogBody}>
              <p>No issue fields available to add.</p>
            </div>
          )}
        </>
      )}
    />
  )
})

try{ AddIssueFieldsDialog.displayName ||= 'AddIssueFieldsDialog' } catch {}
try{ Icon.displayName ||= 'Icon' } catch {}